import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Link, Route, Routes, useLocation } from 'react-router-dom';
import appConfig from "./configuration";
import { initializeApp } from "firebase/app";
import { getDatabase, ref, onValue } from "firebase/database";

import './App.css';
// Landing ('/') stays eagerly imported so the entry route renders without an extra chunk
// round-trip. Every other route is lazy-loaded so its JS (and, for pages like the virtual
// museum or the PDF viewers, its heavy libraries) is only fetched when that route is visited
// instead of being bundled into what every page has to download upfront.
import Landing from './Landing';
import menuIcon from './assets/img/menu.png'
import leftArrow from './assets/img/left-arrow.png'
import NavBar from './components/Accordion/Accordion';
import SideMenu from './components/SideMenu/SideMenu';

const Sights = lazy(() => import('./pages/Sights/Sights'));
const DigitalResources = lazy(() => import('./pages/DigitalResources/DigitalResources'));
const Project = lazy(() => import('./pages/About/Project'));
const Home = lazy(() => import('./pages/Home/Home'));
const Sight = lazy(() => import('./pages/Sights/Sight'));
const Themes = lazy(() => import('./pages/Sights/Themes'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
// News.js has two named exports (no default), so map each to the shape lazy() expects
const News = lazy(() => import('./pages/News/News').then(m => ({ default: m.News })));
const New = lazy(() => import('./pages/News/News').then(m => ({ default: m.New })));
const Idea = lazy(() => import('./pages/Idea/Idea'));
const Team = lazy(() => import('./pages/Team/Team'));
const PhotoAlbums = lazy(() => import('./pages/PhotoAlbum/PhotoAlbums'));
const CalendarPage = lazy(() => import('./pages/Calendar/Calendar'));
const OnFocus = lazy(() => import('./pages/OnFocus/OnFocus'));
const PhotoAlbum = lazy(() => import('./pages/PhotoAlbum/PhotoAlbum'));
const Links = lazy(() => import('./pages/Links/Links'));
const Papers = lazy(() => import('./pages/Papers/Papers'));
const Reports = lazy(() => import('./pages/Reports/Reports'));
const Press = lazy(() => import('./pages/Press/Press'));
const Catalogue = lazy(() => import('./pages/Catalogue/Catalogue'));

const firebaseApp = initializeApp(appConfig);

const Header = () => {
	const [openMenuStyle, setOpenMenuStyle] = useState({});
	const [openMenuIcon, setOpenMenuIcon] = useState(menuIcon);
	const [data, setData] = useState([]);
	const location = useLocation();
	const [currentLocation, setCurrentLocation] = useState(location.pathname);
	const [collapseAllSubmenus, setCollapseAllSubmenus] = useState(false)

	useEffect(() => {
		const database = getDatabase(firebaseApp);

		// Reference to the specific collection in the database
		const collectionRef = ref(database, "objects");
	
		// Function to fetch data from the database
		const fetchData = () => {
			// Listen for changes in the collection
			onValue(collectionRef, (snapshot) => {
			const dataItem = snapshot.val();
	
			// Check if dataItem exists
			if (dataItem) {
				// Convert the object values into an array
				const displayItem = Object.values(dataItem);
				
				setData(displayItem);
			}
			});
		};
	
		// Fetch data when the component mounts
		fetchData();
	}, [])

	const openMenu = () => {
		setOpenMenuStyle({
			left: 0,
		})
		setOpenMenuIcon(
			leftArrow
		)
	}

	const closeMenu = () => {
		setOpenMenuStyle({
			left: '-300px'
		})
		setOpenMenuIcon(
			menuIcon
		)
	}

	const toggleMenu = () => {
		if (openMenuStyle.left === 0) { // opened
			closeMenu();
		}
		else { // closed
			openMenu();
		}
	}

	useEffect(() => {
		if (currentLocation !== location.pathname) {
			setCurrentLocation(location.pathname);
			closeMenu();
		}
	}, [location])

	return (
		<header className="App-header">
			<div className="Menu-icon-wrapper">
				<img className="Menu-icon" onClick={toggleMenu} src={openMenuIcon}/>
			</div>
			<div className="Menu" style={openMenuStyle}>
				<SideMenu collapseAllSubmenus={collapseAllSubmenus}/>
			</div>
			<div className="App-header-nav-items">
				<NavBar collapseAllSubmenus={collapseAllSubmenus} setCollapseAllSubmenus={setCollapseAllSubmenus}/>
			</div>
			<Link className="App-header-logo" to={"/"}><img src={require("./assets/img/logo/logo1.png")} /></Link>
		</header>
	)
}

const Footer = () => {
	return (
		<div className="App-footer">
			<div className="Sponsors-logos">
				<a href='https://bnsf.bg/wps/portal/fond-izsledvaniq/home' target="_blank"><img src={require("./assets/img/sponsors/1.png")} style={{ width: "clamp(90px, 10vw, 160px)", marginBottom: "8px"}}/></a>
				<a href='https://www.balkanstudies.bg/bg/' target="_blank"><img src={require("./assets/img/sponsors/2.png")}/></a>
				<a href='https://www.bas.bg/' target="_blank"><img src={require("./assets/img/sponsors/3.png")}/></a>
			</div>
			<div className="Own-logo">
				<img src={require("./assets/img/logo/logo1.png")}></img>
				<div className="Own-logo-row">
				<p className="Copyright-text">© Copyright, MEDIA CulAge, 2024</p>
				<a href="https://www.youtube.com/@MEDIACulAge" target="_blank">
					<img src={require("./assets/img/youtube-icon-small.png")} style={{ width: "clamp(22px, 3vw, 32px)", margin: "0 8px" }}/>
				</a>
				</div>
			</div>
		</div>
	)
}
const PageLoading = () => (
	<div className="PageLoading">
		<div className="PageLoading-spinner" />
	</div>
)

const App = () => {

	return (
		<div className="App">
			<BrowserRouter>
			<Header/>
			<Suspense fallback={<PageLoading/>}>
				<Routes>
					<Route path="/" element={<Landing/>}/>
					<Route path="/home" element={<Home/>}/>
					<Route exact path="/themes/:type/:themeId" element={<Themes/>}/>
					<Route exact path="/themes/:type/:themeId/:sightsId" element={<Sights/>}/>
					<Route path="/sights/:sightId" element={<Sight/>}/>
					<Route path="/on-focus" element={<OnFocus/>}/>
					<Route path="/digital-resources" element={<DigitalResources/>}/>
					<Route path="/project" element={<Project/>}/>
					<Route path="/papers" element={<Papers/>}/>
					<Route path="/reports" element={<Reports/>}/>
					<Route path="/press" element={<Press/>}/>
					<Route path="/links" element={<Links/>}/>
					<Route path="/news" element={<News/>}/>
					<Route path="/news/:newId" element={<New/>}/>
					<Route path="/idea" element={<Idea/>}/>
					<Route path="/team" element={<Team/>}/>
					<Route path="/contact" element={<Contact/>}/>
					<Route path="/albums/:type/:albumsCategoryId" element={<PhotoAlbums/>}/>
					<Route exact path="/albums/:albumId" element={<PhotoAlbum/>}/>
					<Route path="/catalogue" element={<Catalogue/>}/>
					<Route path="/calendar" element={<CalendarPage/>}/>
				</Routes>
			</Suspense>
			<Footer/>
			</BrowserRouter>
		</div>
	);
}

export default App;
